import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { connect } from 'react-redux';
import List from './List';
import * as Search from 'Actions/search';
import { clearFuturePackages, reloadFuturePackages, reloadRatesAndOffers } from 'Actions/settings';
import * as WidgetPool from 'Actions/widgetPool';
import { currencyShape } from 'Constants/PropTypes';
import { Redirect } from 'Helpers';
import WidgetContext from 'State/WidgetContext';


function sortRates(rates, sortOrder) {
    const [ key, order ] = sortOrder.split('-');

    return rates.sort(getByKeySorter(key, order));
}

function getByKeySorter(key, order) {
    switch (order) {
        case 'desc':
            return (current, next) => current[key] > next[key] ? -1 : 1;

        case 'asc':
        default:
            return (current, next) => current[key] > next[key] ? 1 : -1;
    }
}

/**
 * Provides a list of elements with responsive capabilities, error handling and implementation of grids and layouts
 */
function ListContainer(props) {
    const {
        id, container, getCurrentWidgetSet, rateList, offerList, setSortOrder, isOffer, ratesError,
        offersError, translations, currentLanguage, getOffers, currency, futurePackages, roomCodes,
        roomCategories, packageCodes, packageCategories,
    } = props;
    const configuration = getCurrentWidgetSet(id)[isOffer ? 'offers' : 'rates'];

    useEffect(() => {
        getOffers();
    }, [ getOffers, roomCodes, roomCategories, packageCodes, packageCategories ]);


    // The corresponding list will be passed to the List component within the key 'rates' according
    // to whether it was indicated that this is a list of offers or rates
    let rates = isOffer ? [ ...offerList, ...futurePackages ] : [ ...rateList ];

    // If the key filterByCode is set, only the specified room is sent
    rates = configuration.filterByCode ? rates.filter(room => room.code === configuration.filterByCode) : rates;
    rates = configuration.sortOrder === 'native' ? rates : sortRates(rates, configuration.sortOrder);
    rates = configuration.availableOffersOnly ? rates.filter(room => !!room.price) : rates;

    return (
        <WidgetContext.Provider value={{ id, setSortOrder }}>
            <IntlProvider locale={currentLanguage} messages={translations}>
                <List
                    {...props}
                    currency={currency}
                    rates={rates}
                    container={container}
                    configuration={configuration}
                    error={isOffer ? offersError : ratesError}
                />
            </IntlProvider>
        </WidgetContext.Provider>
    );
}

ListContainer.propTypes = {
    id: PropTypes.string,
    container: PropTypes.object,
    getCurrentWidgetSet: PropTypes.func,
    rateList: PropTypes.array,
    offerList: PropTypes.array,
    futurePackages: PropTypes.array,
    setSortOrder: PropTypes.func,
    isOffer: PropTypes.bool,
    ratesError: PropTypes.bool,
    offersError: PropTypes.bool,
    translations: PropTypes.object,
    currentLanguage: PropTypes.string,
    getOffers: PropTypes.func,
    currency: PropTypes.shape(currencyShape).isRequired,
    roomCodes: PropTypes.string,
    roomCategories: PropTypes.string,
    packageCodes: PropTypes.string,
    packageCategories: PropTypes.string,
};

const mapStateToProps = state => ({
    isLoading: state.rates.isLoading,
    widgetPool: state.widgetPool,
    search: state.search.data,
    rateList: state.rates.data,
    offerList: state.offers.data,
    futurePackages: state.futureOffers?.data || [],
    ratesError: state.rates.error,
    offersError: state.offers.error,
    translations: state.translations.data,
    shouldUpgradeTier: state.search.data && state.search.data.shouldUpgradeTier,
    currency: state.search.data?.currency,
    currentLanguage: state.search.data?.language,
    currencyDisplay: state.settings.data?.currencyDisplay,
    shouldHideButton: state.search.data && state.search.data.shouldHideHotel,
    shouldShowLogin: state.rates.shouldShowLogin,
    newTabDisabled: state.settings.data?.newTabDisabled,
    country: state.settings.data?.country,
    propertyCode: state.url?.propertyCode || state.settings.data?.propertyCode,
    ipAddress: state.settings.data?.ipAddress,
    priceMode: state.channelSettings?.priceCalcMode || 'asInPricelist',
    channelId: state.settings.data?.channelId,
    roomCodes: state.widgetPool[0]?.rates?.roomCodes || '',
    roomCategories: state.widgetPool[0]?.rates?.categories || '',
    packageCodes: state.widgetPool[0]?.offers?.packageCodes || '',
    packageCategories: state.widgetPool[0]?.offers?.categories || '',
});

const mapDispatchToProps = (dispatch, { id: poolId }) => ({
    setSortOrder: (id, widgetType, sortOrder) => dispatch(WidgetPool.setConfig(id, widgetType, { sortOrder })),

    getCurrentWidgetSet: id => dispatch(WidgetPool.getWidgetSet(id)),

    getStringURLParams: async (id, code, isOffer, checkIn, checkOut) => {
        const { query } = await dispatch(Search.getURLParams(id, code, isOffer, null, null, checkIn, checkOut));
        return Object.keys(query).map(key => `${key}=${query[key]}`).join('&');
    },

    searchResults: (id, code, isOffer, newTabDisabled, availableDaterange, checkIn, checkOut) => {
        const urlParams = dispatch(Search.getURLParams(id, code, isOffer, false, availableDaterange, checkIn, checkOut));
        Redirect(urlParams, newTabDisabled);

        const rateEvent = new CustomEvent('rateselected', { bubbles: true, cancelable: false, detail: { code, isOffer } });
        document.dispatchEvent(rateEvent);
    },

    getOffers: async () => {
        try {
            const offers = await dispatch(reloadRatesAndOffers(poolId));
            const widgetSet = dispatch(WidgetPool.getWidgetSet(poolId));
            const offersConfiguration = widgetSet?.offers ?? {};

            // if there is offers list widget on the page, load future packages, if necessary
            if (offersConfiguration.isRendered === true) {
                const { numberOfOffers } = offersConfiguration;

                await dispatch(clearFuturePackages(poolId));
                if (offers && offers.offers.length < numberOfOffers) {
                    await dispatch(reloadFuturePackages(poolId));
                }
            }

            return offers;
        } catch (e) {
            console.error('Could not reload rates and offers', e);
        }
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(ListContainer);
